'use client'

import type { ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { ArrowLeftIcon, ArrowRightIcon, CaretRightIcon } from '@/components/icons/icon-set'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { SiteHeader } from '@/components/docs/site-header'
import { DocsSidebarNav } from '@/components/docs/docs-sidebar'
import { getNeighbours } from '@/constants/docs-nav'

/*
 * Docs shell, structured like shadcn's docs: sticky top bar, grouped sidebar, the page,
 * an "On this page" outline that follows your scrolling, and previous and next links.
 * Styling is GoodUnited's own: every color, type size and radius comes from the tokens.
 */
export interface TocItem {
    id: string
    title: string
    depth?: 2 | 3
}

export function SiteFooter() {
    return (
        <footer className='border-t border-border-subtle'>
            <div className='mx-auto flex max-w-384 flex-col gap-1 px-4 py-6 text-sm text-text-secondary sm:px-6 lg:px-8'>
                <p>
                    GoodUnited design system. The Figma file is the source of truth for every color, type size and component.
                </p>
            </div>
        </footer>
    )
}

function TableOfContents({ items }: { items: TocItem[] }) {
    const [active, setActive] = useState(items[0]?.id ?? '')

    useEffect(() => {
        const elements = items.map(item => document.getElementById(item.id)).filter((element): element is HTMLElement => !!element)
        if (!elements.length) return

        const observer = new IntersectionObserver(entries => {
            const visible = entries.filter(entry => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
            if (visible[0]) setActive(visible[0].target.id)
        }, { rootMargin: '-80px 0px -70% 0px' })

        elements.forEach(element => observer.observe(element))
        return () => observer.disconnect()
    }, [items])

    return (
        <nav aria-label='On this page' className='flex flex-col gap-2 text-sm'>
            <p className='font-medium text-text-primary'>
                On this page
            </p>
            <ul className='flex flex-col gap-1.5'>
                {items.map(item => (
                    <li key={item.id}>
                        <a
                            href={`#${item.id}`}
                            className={cn(
                                'block rounded-sm leading-snug transition-colors outline-none focus-visible:outline-2 focus-visible:outline-border-focus',
                                item.depth === 3 && 'pl-3',
                                active === item.id ? 'font-medium text-text-primary' : 'text-text-secondary hover:text-text-primary'
                            )}
                        >
                            {item.title}
                        </a>
                    </li>
                ))}
            </ul>
        </nav>
    )
}

function NeighbourButtons({ href }: { href: string }) {
    const { previous, next } = getNeighbours(href)

    return (
        <div className='flex shrink-0 items-center gap-2'>
            {previous && (
                <Button variant='outline' size='icon-sm' render={<Link href={previous.href} />} nativeButton={false} aria-label={`Previous: ${previous.title}`}>
                    <ArrowLeftIcon aria-hidden='true' />
                </Button>
            )}
            {next && (
                <Button variant='outline' size='icon-sm' render={<Link href={next.href} />} nativeButton={false} aria-label={`Next: ${next.title}`}>
                    <ArrowRightIcon aria-hidden='true' />
                </Button>
            )}
        </div>
    )
}

interface DocsPageHeaderProps {
    title: string
    description: string
    group?: string
    figma?: string
    href?: string
}

/* Page heading: breadcrumb, Poppins / text-3xl / semibold title, description and the Figma page it maps to. */
export function DocsPageHeader({ title, description, group, figma, href }: DocsPageHeaderProps) {
    return (
        <div className='flex flex-col gap-3'>
            <div className='flex items-center justify-between gap-4'>
                <nav aria-label='Breadcrumb' className='flex items-center gap-1.5 text-sm text-text-secondary'>
                    <Link href='/docs' className='rounded-sm outline-none hover:text-text-primary focus-visible:outline-2 focus-visible:outline-border-focus'>
                        Docs
                    </Link>
                    {group && (
                        <>
                            <CaretRightIcon className='size-4' aria-hidden='true' />
                            <span>
                                {group}
                            </span>
                        </>
                    )}
                </nav>
                {href && <NeighbourButtons href={href} />}
            </div>
            <h1 className='font-heading text-3xl font-semibold text-text-primary'>
                {title}
            </h1>
            <p className='max-w-3xl text-base text-text-secondary'>
                {description}
            </p>
            {figma && (
                <Badge variant='secondary' className='mt-1'>
                    {`Figma: ${figma}`}
                </Badge>
            )}
        </div>
    )
}

/* Previous and next pages at the bottom. */
export function DocsPager({ href }: { href: string }) {
    const { previous, next } = getNeighbours(href)

    return (
        <div className='flex flex-col gap-3 border-t border-border-subtle pt-8 sm:flex-row sm:justify-between'>
            {previous
                ? (
                    <Link href={previous.href} className='group flex flex-col gap-1 rounded-lg border border-border-subtle px-4 py-3 transition-colors outline-none hover:bg-surface-subtle focus-visible:outline-2 focus-visible:outline-border-focus sm:min-w-56'>
                        <span className='inline-flex items-center gap-1 text-sm text-text-secondary'>
                            <ArrowLeftIcon className='size-4' aria-hidden='true' />
                            Previous
                        </span>
                        <span className='text-base font-medium text-text-primary'>
                            {previous.title}
                        </span>
                    </Link>
                )
                : <span />}
            {next && (
                <Link href={next.href} className='group flex flex-col items-end gap-1 rounded-lg border border-border-subtle px-4 py-3 text-right transition-colors outline-none hover:bg-surface-subtle focus-visible:outline-2 focus-visible:outline-border-focus sm:min-w-56'>
                    <span className='inline-flex items-center gap-1 text-sm text-text-secondary'>
                        Next
                        <ArrowRightIcon className='size-4' aria-hidden='true' />
                    </span>
                    <span className='text-base font-medium text-text-primary'>
                        {next.title}
                    </span>
                </Link>
            )}
        </div>
    )
}

interface DocsSectionProps {
    id: string
    title: string
    description?: ReactNode
    level?: 2 | 3
    children?: ReactNode
    className?: string
}

/* A titled docs section. h2 for sections, h3 for examples; the id feeds the outline and anchors. */
export function DocsSection({ id, title, description, level = 2, children, className }: DocsSectionProps) {
    const Heading = level === 2 ? 'h2' : 'h3'

    return (
        <section aria-labelledby={id} className={cn('flex scroll-mt-24 flex-col gap-4', className)}>
            <div className='flex flex-col gap-1'>
                <Heading id={id} className={cn('scroll-mt-24 font-heading font-semibold text-text-primary', level === 2 ? 'text-2xl' : 'text-lg')}>
                    <a href={`#${id}`} className='rounded-sm outline-none hover:underline focus-visible:outline-2 focus-visible:outline-border-focus'>
                        {title}
                    </a>
                </Heading>
                {description && (
                    <div className='max-w-3xl text-base text-text-secondary'>
                        {description}
                    </div>
                )}
            </div>
            {children}
        </section>
    )
}

interface DocsLayoutProps {
    children: ReactNode
    toc?: TocItem[]
}

export function DocsLayout({ children, toc }: DocsLayoutProps) {
    const router = useRouter()

    // Jump to the anchor once the page has rendered (frames and outlines load after the first paint).
    useEffect(() => {
        const hash = router.asPath.split('#')[1]
        if (!hash) return
        const timer = window.setTimeout(() => document.getElementById(decodeURIComponent(hash))?.scrollIntoView(), 120)
        return () => window.clearTimeout(timer)
    }, [router.asPath])

    return (
        <div className='flex min-h-dvh flex-col bg-surface-page text-text-primary'>
            <SiteHeader />
            <div className='mx-auto grid w-full max-w-384 flex-1 grid-cols-1 gap-10 px-4 sm:px-6 lg:grid-cols-[220px_minmax(0,1fr)] lg:px-8'>
                <aside className='no-scrollbar sticky top-14 hidden h-[calc(100dvh-3.5rem)] overflow-y-auto py-8 lg:block'>
                    <DocsSidebarNav />
                </aside>
                <div className={cn('grid min-w-0 grid-cols-1 gap-10 py-8 lg:py-10', toc?.length && 'xl:grid-cols-[minmax(0,1fr)_200px]')}>
                    <main id='main' className='flex min-w-0 flex-col gap-12'>
                        {children}
                    </main>
                    {toc && toc.length > 0 && (
                        <aside className='hidden xl:block'>
                            <div className='no-scrollbar sticky top-24 max-h-[calc(100dvh-8rem)] overflow-y-auto'>
                                <TableOfContents items={toc} />
                            </div>
                        </aside>
                    )}
                </div>
            </div>
            <SiteFooter />
        </div>
    )
}
